import { useEffect } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Loading from "../loading";
import { useSchoolFeeFeeAssignment } from "../../api/queries";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { formatCurrency, getFailureDisplay, getStorageToken } from "../../utils";
import { SchoolFeeFeeAssignmentLine } from "../../models/schoolFee";

/**
 * The fees payable on a placement the payer browsed to, for journeys B and C.
 *
 * ⚠ It is a DIFFERENT source from journey A's, not a different rendering of
 * the same one: A reads its fees off the student-search hit, which carries a
 * currency, while `/schoolfee/fee-assignment` carries none. A's always carries
 * the partial-payment flag; this one from customer portal API 2.1.0 only.
 * `FeeSelect` is journey A's and stays that way -- one
 * component switching on which shape it was handed is how the two drift.
 *
 * ⛔ No client-side filtering by `variant` (TODO-187): the server returns the
 * fees of the service the payer arrived through, and duplicating that rule
 * here is how the payer comes to see a set the payment leg disagrees with.
 */
const CatalogueFeeSelect = ({
  coreServiceId,
  currency,
  fixedFullAmount = false,
}: {
  coreServiceId: string;
  // ⚠ The SERVICE's currency, not the fee's: /schoolfee/fee-assignment carries
  // none, unlike the fees nested in a search hit. It is passed in rather than
  // assumed here, because the alternative is XAF hardcoded on a money screen.
  currency?: string;
  // Registration (D-05): the amount is the whole fee, shown and not editable.
  // ⛔ It is a convenience, not the enforcement -- the server refuses a partial
  // registration payment with CPS-422-111 whatever the screen renders.
  fixedFullAmount?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const token = getStorageToken();
  const { selectionId, selectedFee, selectFee, amount, setAmount } =
    useSchoolFee();

  const assignment = useSchoolFeeFeeAssignment({
    coreServiceId,
    selectionId,
    authorisation: token || "",
  });

  const fees = assignment.data?.items ?? [];

  // `supports_partial_payments` is copied as served. An older server omits it,
  // and the absence is kept rather than guessed: downstream it reads as "the
  // server decides", while an explicit false asks for the full amount.
  //
  // `currency` comes from the SERVICE, which does declare one. Carrying it on
  // the selection is what lets the amount and confirmation screens name a unit
  // without any of them falling back to a hardcoded XAF.
  const asSelected = (fee: SchoolFeeFeeAssignmentLine) => ({
    fee_assignment_id: fee.fee_assignment_id,
    name: fee.name,
    variant: fee.variant,
    amount: fee.amount,
    currency,
    supports_partial_payments: fee.supports_partial_payments,
  });

  const only = fees.length === 1 ? fees[0] : null;

  // One fee is not a choice, so it is taken rather than offered.
  useEffect(() => {
    if (!only) return;
    if (selectedFee?.fee_assignment_id === only.fee_assignment_id) return;

    selectFee(asSelected(only));
    // asSelected is a pure projection of `only`; adding it to the dependency
    // list would re-run this on every render for no change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only, selectedFee, selectFee]);

  // The registration amount follows the fee and nothing else. `selectFee`
  // clears a stale amount whenever the fee changes, so this writes it back
  // afterwards rather than racing it.
  useEffect(() => {
    if (!fixedFullAmount) return;

    const full = selectedFee?.amount ?? "";

    if (full === "" || amount === full) return;

    setAmount(full);
  }, [fixedFullAmount, selectedFee, amount, setAmount]);

  if (assignment.isFetching) return <Loading />;

  if (assignment.isError) {
    return (
      <Box mb={4}>
        <Alert severity="error">
          {getFailureDisplay(assignment.error, t, i18n.language).message}
        </Alert>
      </Box>
    );
  }

  if (fees.length === 0) {
    return (
      <Box mb={4} data-testid="school-fee-catalogue-fees-empty">
        <Typography variant="body1" fontWeight="700">
          {t("schoolfee.fee.none_title")}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          {t("schoolfee.fee.none_body")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("schoolfee.fee.select_label")}
      </Typography>

      {only ? (
        <Box mb={2} data-testid="school-fee-single-fee">
          <Typography variant="body1" fontWeight="700">
            {only.name}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {formatCurrency(only.amount, undefined, currency)}
          </Typography>
        </Box>
      ) : (
        <RadioGroup
          value={selectedFee?.fee_assignment_id ?? ""}
          onChange={event => {
            const chosen = fees.find(
              fee => fee.fee_assignment_id === event.target.value
            );

            selectFee(chosen ? asSelected(chosen) : null);
          }}
        >
          {fees.map(fee => (
            <FormControlLabel
              key={fee.fee_assignment_id}
              value={fee.fee_assignment_id}
              control={<Radio />}
              label={`${fee.name} — ${formatCurrency(
                fee.amount,
                undefined,
                currency
              )}`}
            />
          ))}
        </RadioGroup>
      )}

      {/* ⛔ Registration: rendered as TEXT, never as a disabled input. A
          read-only input is still an input in the DOM, and D-05 is that no
          partial-amount control exists on this journey at all. */}
      {fixedFullAmount && (
        <Box mt={3} data-testid="school-fee-registration-amount">
          <Typography sx={{ fontSize: 13 }}>
            {t("schoolfee.registration.amount_label")}
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {formatCurrency(selectedFee?.amount ?? "0", undefined, currency)}
          </Typography>
          <Typography sx={{ fontSize: 13 }} mt={1}>
            {t("schoolfee.registration.fee_full_hint")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CatalogueFeeSelect;
